import { attachment, edit } from '@/router/utils'
import { EditButton } from '@/shared/components/action-buttons'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { Pagination } from '@/shared/components/pagination'
import { useGetCompaniesQuery } from '@/store/services'
import { Chip } from '@material-tailwind/react'
import { HasAccess } from '@permify/react-role'
import { usePagination } from '@table-library/react-table-library/pagination'
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'
import { useTheme } from '@table-library/react-table-library/theme'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { FiFolder } from 'react-icons/fi'
import { Link, useSearchParams } from 'react-router-dom'
export const CompanyTable = () => {
  const { data: { companies = [], meta = {} } } = useGetCompaniesQuery(location.search, {
    skip: isEmpty(location.search)
  })
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const formatDateDMY = (s) => {
    if (!s) return ''
    const [y, m, d] = s.split('-')
    return `${d}-${m}-${y}`
  }

  const onPaginationChange = (_, state) => {
    searchParams.set('page', state.page + 1)
    setSearchParams(searchParams)
  }

  const nodes = { nodes: companies }

  const pagination = usePagination(
    nodes,
    {
      state: {
        page: Number(searchParams.get('page')) - 1
      },
      onChange: onPaginationChange
    },
    {
      isServer: true
    }
  )

  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr 2fr 1fr 1fr 1fr 2fr;
    `,
    BaseCell: `
      &:nth-of-type(11) {
        right: 0px;
      }
    `
  })
  return (

    <div className='flex flex-col justify-between gap-4 h-full'>
      <Table
        data={nodes}
        pagination={pagination}
        className='h-max z-0'
        layout={{ custom: true, fixedHeader: true }}
        theme={theme}
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>RUT</HeaderCell>
                <HeaderCell>Razón social</HeaderCell>
                <HeaderCell>Áreas de Servicio</HeaderCell>
                <HeaderCell>Contratos activos</HeaderCell>
                <HeaderCell>Personas activas</HeaderCell>
                <HeaderCell>Usuario principal</HeaderCell>
                <HeaderCell>Fecha de creación</HeaderCell>
                <HeaderCell>Estado</HeaderCell>
                <HeaderCell>Acreditado</HeaderCell>
                <HeaderCell>NC</HeaderCell>
                <HeaderCell pinRight>{t('actions.other')}</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map((item) => (
                <Row key={item.id} item={item}>
                  <Cell>{item.documentNumber}</Cell>
                  <Cell>
                    <div className='flex flex-col leading-tight'>
                      <span>{item.name}</span>
                      {item.tradeName && (
                        <span className='italic text-sm text-gray-700'>{item.tradeName}</span>
                      )}
                    </div>
                  </Cell>
                  <Cell className='w-full'>
                    <div className='flex flex-wrap gap-1 items-start justify-start'>
                      {item.serviceAreas.map((serviceArea) => (
                        <Chip
                          key={serviceArea.id}
                          variant='ghost'
                          color='blue'
                          size='sm'
                          className='capitalize text-center w-fit h-fit self-start'
                          value={serviceArea.name}
                        />
                      ))}
                    </div>
                  </Cell>
                  <Cell>{item.activeContractsCount}</Cell>
                  <Cell>{item.activeEmployeesCount}</Cell>
                  <Cell>{item.user?.email}</Cell>
                  <Cell>{item.createdAt}</Cell>
                  <Cell>
                    <div className='flex items-start justify-start'>
                      <Chip
                        variant='ghost'
                        color={item.status === 'active' ? 'green' : 'red'}
                        size='sm'
                        className='capitalize text-center self-start h-fit w-fit'
                        value={
                          item.status === 'active'
                            ? t('common.active')
                            : t('common.inactive')}
                      />
                    </div>
                  </Cell>
                  <Cell>
                    <div className='flex flex-col items-start gap-1'>
                      <Chip
                        variant='ghost'
                        color={item.accredited === 'active' ? 'green' : 'red'}
                        size='sm'
                        className='capitalize text-center h-fit w-fit'
                        value={
                          item.accredited === 'active'
                            ? t('common.true')
                            : t('common.false')
                        }
                      />
                      {item.accredited === 'active' && item.accreditationExpiration && (
                        <div className='flex flex-col leading-tight'>
                          <span className='text-xs text-gray-700'>Vence:</span>
                          <span className='text-xs text-gray-700'>
                            {formatDateDMY(item.accreditationExpiration)}
                          </span>
                        </div>
                      )}
                    </div>
                  </Cell>
                  <Cell>{item.nonconformities}</Cell>
                  <Cell pinRight>
                    <div className='flex gap-2'>
                      <HasAccess
                        permissions="company#edit"
                      >
                        <Link to={edit('companies', item.id)}>
                          <EditButton />
                        </Link>
                      </HasAccess>

                      <HasAccess
                        permissions="company#attachments"
                      >
                        <LinkWithQuery to={attachment('companies', item.id)}>
                          <FiFolder className='cursor-pointer' size='1rem' />
                        </LinkWithQuery>
                      </HasAccess>
                    </div>
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
      <Pagination
        onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
        page={pagination.state.page}
        totalPage={meta?.pagination?.total_pages}
      />
    </div>
  )
}
